import React, { useEffect, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import { Colors } from 'library';
import { client, consoleLogInDev } from 'shared';
import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';

const styles = {
    label: {
        fontFamily: 'Outfit',
        color: Colors.darkNavy,
    },
    errorText: {
        fontFamily: 'Outfit',
        color: Colors.error,
        fontSize: 12,
        margin: 0,
    },
};

const StyledInput = styled('input')({
    '&:hover': {
        outline: 'none',
    },
    '&:focus': {
        outline: 'none',
    },
    '&::placeholder': {
        color: Colors.textSecondary,
    },
    borderBottomColor: Colors.border,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderWidth: 0,
    borderBottomWidth: 1,
    width: '100%',
});

export default function ReferralCodeInput({
    referralCode,
    setReferralCode,
    valid,
    setValid,
}: {
    referralCode: string;
    setReferralCode: (referralCode: string) => void;
    valid: boolean;
    setValid: (valid: boolean) => void;
}) {
    const [showErrors, setShowErrors] = useState(false);
    const [cookies, setCookie, removeCookie] = useCookies(['referral']);
    const location = useLocation();

    useEffect(() => {
        const referral = new URLSearchParams(location.search).get('referral') ?? cookies.referral;
        setReferralCode(referral);
        validateReferralCode(referral);
    }, []);

    function validateReferralCode(code: string) {
        setShowErrors(true);
        referralCode ? setValid(false) : setValid(true);
        if (code) {
            client(`api/referral-code/validate/?code=${code}`)
                .then((response: { id: number }) => {
                    if (response.id !== null) {
                        setValid(true);
                        setCookie('referral', code, { path: '/' });
                    } else {
                        setValid(false);
                        removeCookie('referral');
                    }
                })
                .catch((error) => {
                    consoleLogInDev(error);
                    setValid(true);
                });
        }
    }

    function onValueChanged(event: any) {
        setReferralCode(event.target.value.toUpperCase());
    }

    function getPlaceholder() {
        return referralCode ? '' : 'Referral Code (Optional)';
    }

    function getLabel() {
        return !referralCode ? '' : 'Referral Code';
    }

    return (
        <Grid container direction="column">
            <Grid item>
                <label style={styles.label}>{getLabel()}</label>
            </Grid>
            <Grid item>
                <StyledInput
                    type="text"
                    value={referralCode}
                    onChange={onValueChanged}
                    placeholder={getPlaceholder()}
                    style={{ color: Colors.darkNavy }}
                />
            </Grid>
            {showErrors && !valid ? (
                <Grid container item alignItems="center">
                    <Grid item style={{ paddingLeft: 5 }}>
                        <CancelIcon style={{ color: Colors.error, maxHeight: 12 }} />
                    </Grid>
                    <Grid item>
                        <p style={styles.errorText}>{showErrors && !valid && 'Invalid referral code'}</p>
                    </Grid>
                </Grid>
            ) : null}
        </Grid>
    );
}
